import { Power2, gsap } from 'gsap'
import React, { CSSProperties, FC, MouseEventHandler, ReactNode, useCallback, useEffect, useRef } from 'react'
import { Transition, TransitionGroup } from 'react-transition-group'

import { Close } from '@mui/icons-material'
import { GRAY_800 } from 'constants/styling/theme'
import { KeyboardEventKey } from 'constants/misc'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDivider } from 'components/common/MUIDivider/MUIDivider.component'
import { QueryDataType } from 'components/common/QueryStatus'
import classnames from 'classnames'
import { createPortal } from 'react-dom'
import styles from './Modal.module.sass'
import { YIELDS_FOCUS_ATTRIBUTE, hasOpenAccessibleDialog, useAccessibleDialogStack, useRegisterPortalModal } from 'utils/hooks/portalModalRegistry'
import { useStateRef } from 'utils/hooks'

/** What Tab can reach inside an accessible dialog. */
const FOCUSABLE = ['a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])', 'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]']
  .map(selector => `${selector}:not([tabindex="-1"])`)
  .join(', ')

/**
 * MUI lists (Menu, Popover, a portalled Popper) render at the end of the body: focus there is still the dialog's.
 * Not `[role="presentation"]`: a Drawer or Dialog behind the dialog carries it too.
 */
const MUI_OVERLAY = '.MuiPopper-root, .base-Popper-root, .MuiPopover-root, .MuiMenu-root'

const SIZE_CLASS: Record<NonNullable<Props['size']>, string> = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
}

let generatedDialogIds = 0

const getFocusables = (root: HTMLElement) => Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
  .filter(element => !element.closest('[aria-hidden="true"], [hidden], [inert]'))

/** Focuses the target as soon as it can take focus: the GSAP fade-in starts the dialog at `visibility: hidden`. */
const focusWhenShown = (getTarget: () => HTMLElement | null) => {
  let frame = 0
  let attempts = 0
  const attempt = () => {
    const target = getTarget()
    target?.focus()
    if (target && document.activeElement !== target && attempts++ < 30) frame = requestAnimationFrame(attempt)
  }
  attempt()

  return () => cancelAnimationFrame(frame)
}

/**
 * @interface BaseProps Input properties
 */
interface BaseProps {
  /** Modal id */
  id?: string
  /** The additional classes to append */
  className?: string
  /** The additional classes to append to the modal content element */
  modalContentClassName?: string
  /** The additional classes to append to the modal's scrollable content element */
  scrollableContentClassName?: string
  /** Decides when is modal open */
  isOpen: boolean,
  /** Modal title */
  title?: string | ReactNode
  /** Modal subtitle */
  subtitle?: string | ReactNode
  /** Onclick action triggered when user clicks outside the modal content element */
  onClickOutside?: (e: React.MouseEvent) => void
  /** onKeyDown action triggered when user presses any key on the keyboard */
  onKeyDown?: (e: React.KeyboardEvent) => void
  /** onClick action to close Modal */
  onClose?: (e: React.MouseEvent) => void
  /** Timeline animation effect for enter transition */
  enterEffect?: { (node: HTMLElement, appears: boolean): void }
  /** Timeline animation effect for exit transition */
  exitEffect?: { (node: HTMLElement): void }
  /** Action to be triggered after modal is visibly closed */
  afterClosed?: () => void
  /** Timeline for enter transition */
  timelineEnter?: gsap.core.Timeline
  /** Timeline for exit transition */
  timelineExit?: gsap.core.Timeline
  /** Timeout for enter transition, default=600 */
  timeoutEnter?: number
  /** Timeout for exit transition, default=300 */
  timeoutExit?: number
  /** Whether title and body is divided */
  hasHeaderContentDivider?: boolean
  /** Footer content component with custom styles */
  footerContent?: ReactNode
  /** Whether click events propagation should be disabled or not (default false) */
  stopClickEventPropagation?: boolean
  /** React query call data */
  queryData?: QueryDataType
  /** Extra content displayed in header under title/subtitle (if provided) */
  extraHeaderContent?: ReactNode
  /** CSS rules for content */
  contentStyle?: CSSProperties
  /** Optional z-index for the modal overlay */
  zIndex?: number
  /** Width preset: at most 48 / 88 / 120rem, and the viewport minus 1.6rem a side below that. Without it the modal is as wide as its content. */
  size?: 'sm' | 'md' | 'lg'
  /** What sits left of `footerContent`, in the same row: a count, a pager */
  footerStart?: ReactNode
  /** `alertdialog` for a confirmation (default `dialog`). Only with `isAccessibleDialog` */
  dialogRole?: 'dialog' | 'alertdialog'
  /** Id of the element that describes the dialog. Only with `isAccessibleDialog` */
  ariaDescribedBy?: string
  /** Below the `sm` breakpoint: a full-width sheet rising from the bottom of the screen, at most 92dvh tall */
  isSheetOnPhone?: boolean
  children?: ReactNode
}

/**
 * `isAccessibleDialog`: dialog semantics and focus handling: `role`, `aria-modal`, named by `title`; focus moves in on
 * open (to `[data-autofocus]`, else the first focusable element), Tab stays inside, focus returns to the opener on close.
 * Of several open accessible dialogs, only the last opened answers Escape and Tab.
 * Its close button, rendered by `onClose`, then needs `closeLabel` as its accessible name.
 */
type AccessibleDialogProps =
  | { isAccessibleDialog?: false, closeLabel?: string }
  | { isAccessibleDialog: true, onClose?: undefined, closeLabel?: string }
  | { isAccessibleDialog: true, onClose: (e: React.MouseEvent) => void, closeLabel: string }

export type Props = BaseProps & AccessibleDialogProps

/**
 * @component Display overlay popup with highly customize content requiring user's interaction, normally use in showing multiple info or actions.
 * @example
 * <Modal isOpen={true}>
 *  <span>Content of the modal</span>
 * </Modal>
 */
const Modal: FC<Props> = ({
  id,
  children,
  className = '',
  modalContentClassName = '',
  contentStyle,
  scrollableContentClassName,
  isOpen = false,
  title = '',
  subtitle,
  onClickOutside,
  onKeyDown,
  onClose,
  timelineEnter = gsap.timeline({ paused: true }),
  timelineExit = gsap.timeline({ paused: true }),
  enterEffect = (node: HTMLElement, appears: boolean) => {
    if (!node) return
    if (node.nodeType === node.TEXT_NODE) return
    timelineEnter.totalProgress(1).clear(true).totalProgress(0)
    timelineEnter.fromTo(node, { autoAlpha: 0, ease: Power2.easeIn }, { autoAlpha: 1, ease: Power2.easeIn, duration: 0.3 }, 0)
    timelineEnter.play()
  },
  exitEffect = (node: HTMLElement) => {
    if (!node) return
    if (node.nodeType === node.TEXT_NODE) return
    timelineExit.totalProgress(1).clear(true).totalProgress(0)
    timelineExit.fromTo(node, { autoAlpha: 1, ease: Power2.easeIn }, { autoAlpha: 0, ease: Power2.easeIn, duration: 0.3 }, 0)
    timelineExit.play()
  },
  timeoutEnter = 600,
  timeoutExit = 300,
  afterClosed,
  hasHeaderContentDivider = false,
  stopClickEventPropagation = false,
  extraHeaderContent,
  footerContent,
  queryData,
  zIndex,
  size,
  footerStart,
  isAccessibleDialog = false,
  dialogRole = 'dialog',
  ariaDescribedBy,
  closeLabel,
  isSheetOnPhone = false,
}) => {
  const modalContentRef = useRef<HTMLDivElement>(null)
  const isOpenRef = useStateRef(isOpen)
  // Not React's useId: its counter is global, and the storyshots keep the ids it generates verbatim
  const generatedIdRef = useRef('')
  if (isAccessibleDialog && !id && !generatedIdRef.current) generatedIdRef.current = `modal-dialog-${++generatedDialogIds}`
  const titleId = `${id || generatedIdRef.current}-title`

  useRegisterPortalModal(isOpen)
  const isTopDialog = useAccessibleDialogStack(isAccessibleDialog && isOpen)
  const hasClose = !!onClose

  useEffect(() => {
    if (import.meta.env.DEV && isAccessibleDialog && hasClose && !closeLabel) console.warn('Modal: an accessible dialog with onClose needs a closeLabel')
  }, [isAccessibleDialog, hasClose, closeLabel])

  /** Wraps Tab and Shift+Tab around the ends of the dialog, and brings back focus that got out of it. */
  const keepTabInside = useCallback((e: KeyboardEvent) => {
    const content = modalContentRef.current
    if (!content || e.defaultPrevented) return
    const active = document.activeElement
    // A list portalled out of the dialog handles its own Tab
    if (active && !content.contains(active) && active.closest(MUI_OVERLAY)) return

    const focusables = getFocusables(content)
    const first = focusables[0] ?? content
    const last = focusables[focusables.length - 1] ?? content
    if (!active || !content.contains(active)) {
      e.preventDefault()
      ;(e.shiftKey ? last : first).focus()
    } else if (e.shiftKey && (active === first || active === content)) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && active === last) {
      e.preventDefault()
      first.focus()
    }
  }, [])

  // Accessible dialog: focus moved in, kept in, and given back to the opener on close
  useEffect(() => {
    if (!isAccessibleDialog || !isOpen) return
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const cancelInitialFocus = focusWhenShown(() => {
      const content = modalContentRef.current
      if (!content) return null
      return content.querySelector<HTMLElement>('[data-autofocus]:not([data-autofocus="false"])') ?? getFocusables(content)[0] ?? content
    })

    const onFocusIn = (e: FocusEvent) => {
      const content = modalContentRef.current
      const target = e.target instanceof HTMLElement ? e.target : null
      if (!content || !target || !isTopDialog()) return
      if (content.contains(target) || target.closest(MUI_OVERLAY)) return
      // A MUI Dialog or Drawer keeping its own focus trap would pull focus straight back: pulling too never ends
      const muiModal = target.closest('.MuiModal-root')
      if (muiModal && !muiModal.hasAttribute(YIELDS_FOCUS_ATTRIBUTE)) return
      (getFocusables(content)[0] ?? content).focus()
    }
    document.addEventListener('focusin', onFocusIn)

    return () => {
      cancelInitialFocus()
      document.removeEventListener('focusin', onFocusIn)
      if (opener && opener !== document.body && opener.isConnected) opener.focus()
    }
  }, [isAccessibleDialog, isOpen, isTopDialog])

  /** action called upon clicking anywhere */
  const clickAction: MouseEventHandler<HTMLDivElement> = useCallback((e: any) => {

    if (stopClickEventPropagation) e.stopPropagation()

    // Modal is not opened
    if (!isOpenRef.current) return

    // A click in a modal portalled from this one's children bubbles here through the React tree: not ours
    if (!e.currentTarget.contains(e.target)) return

    // Clicked inside the modal content
    if (modalContentRef.current?.contains(e.target)) return

    // onClickOutside action exists
    if (onClickOutside) onClickOutside(e)
  }, [stopClickEventPropagation, isOpenRef, onClickOutside])

  /** action called upon pressing any keyboard key, with default close action in pressing Esc */
  const keyAction = useCallback((e: any) => {
    // Modal is not opened
    if (!isOpenRef.current) return

    if (isAccessibleDialog) {
      if (!isTopDialog()) return
      if (e.key === KeyboardEventKey.TAB) keepTabInside(e)
      // An Escape that a list or a select inside already used is not the dialog's
      if (e.key === KeyboardEventKey.ESCAPE && e.defaultPrevented) return
    } else if (e.key === KeyboardEventKey.ESCAPE && hasOpenAccessibleDialog()) {
      // The accessible dialog open over this one closes first
      return
    }

    // Customize onKeyDown actions
    if (onKeyDown) return onKeyDown(e)
    // Default Esc keyDown to close Modal
    if (e.key === KeyboardEventKey.ESCAPE) {
      if (isAccessibleDialog) e.preventDefault()
      onClickOutside && onClickOutside(e)
    }
  }, [isOpenRef, isAccessibleDialog, isTopDialog, keepTabInside, onKeyDown, onClickOutside])


  // Bind key press event listeners
  useEffect(() => {
    document.addEventListener('keydown', keyAction)

    return () => {
      document.removeEventListener('keydown', keyAction)
    }
  }, [keyAction])

  const handleClose = useCallback((node: HTMLElement) => {
    if (!!afterClosed) setTimeout(afterClosed, timeoutExit)
    exitEffect(node)
  }, [afterClosed, exitEffect, timeoutExit])

  return createPortal((
    <TransitionGroup component={null}>
      {isOpen &&
        <Transition
          appear={true}
          in={isOpen}
          onEnter={(node, appears) => enterEffect(node, appears)}
          onExit={(node) => handleClose(node)}
          timeout={{ enter: timeoutEnter, exit: timeoutExit }}
        >
          <div
            id={id}
            className={`${styles.modal} ${className} ${!!footerContent && styles.footer}${isSheetOnPhone ? ` ${styles.sheetOnPhone}` : ''}`.trim()}
            onClick={clickAction}
            style={{ zIndex: zIndex || 100 }}
          >
            <div
              className={`${styles.modalContent} ${modalContentClassName} ${classnames(size && SIZE_CLASS[size], { [styles.sheetOnPhone]: isSheetOnPhone })}`.trim()}
              ref={modalContentRef}
              {...(isAccessibleDialog && {
                role: dialogRole,
                'aria-modal': true,
                'aria-labelledby': title ? titleId : undefined,
                'aria-describedby': ariaDescribedBy,
                tabIndex: -1,
              })}
            >

              {!!onClose &&
                <MUIButton isIconButton sx={{ width: '3.5rem', position: 'absolute', top: '2rem', right: '2.5rem', zIndex: 100 }} type="secondaryBorder" onClick={onClose} aria-label={closeLabel}>
                  <Close fontSize="large" htmlColor={GRAY_800} />
                </MUIButton>
              }

              {(!!title || !!subtitle || !!extraHeaderContent) &&
                <div className={styles.header}>

                  {!!title &&
                    <div
                      id={isAccessibleDialog ? titleId : undefined}
                      className={classnames(
                        styles.title,
                        { [styles.hasSubtitle]: !!subtitle }
                      )}
                    >
                      {title}
                    </div>
                  }

                  {!!subtitle &&
                    <div className={styles.subtitle}>
                      {subtitle}
                    </div>
                  }

                  {extraHeaderContent}

                </div>
              }

              {hasHeaderContentDivider && <MUIDivider className={styles.fullWidthDivider} margin={2} />}

              <div className={classnames(styles.scrollableContent, scrollableContentClassName)} style={contentStyle}>
                {children}
              </div>

              {(!!footerContent || !!footerStart) &&
                <div className={styles.footer}>
                  {!(queryData?.isSuccess || queryData?.error) && <MUIDivider className={styles.fullWidthDivider} margin={8} />}
                  {!!footerStart
                    ? (
                      <div className={styles.footerRow}>
                        <div className={styles.footerStart}>{footerStart}</div>
                        {footerContent}
                      </div>
                    )
                    : footerContent
                  }
                </div>
              }

            </div>
          </div>
        </Transition>
      }
    </TransitionGroup>
  ), document.querySelector('.App') || document.getElementById('root') || document.body)
}

export default Modal
